import axios from "axios";
import { createContext, useContext, useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "kiltro_admin_token";

export const adminClient = axios.create({ baseURL: API });

adminClient.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

adminClient.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      if (typeof window !== "undefined" && !window.location.pathname.endsWith("/admin/login")) {
        window.location.href = "/admin/login";
      }
    }
    return Promise.reject(err);
  },
);

export const adminApi = {
  login: (email, password) => adminClient.post("/admin/login", { email, password }).then((r) => r.data),
  me: () => adminClient.get("/admin/me").then((r) => r.data),
  changePassword: (current_password, new_password) =>
    adminClient.put("/admin/me/password", { current_password, new_password }).then((r) => r.data),
  // dashboard
  dashboard: () => adminClient.get("/admin/dashboard").then((r) => r.data),
  // users
  listUsers: () => adminClient.get("/admin/users").then((r) => r.data),
  createUser: (payload) => adminClient.post("/admin/users", payload).then((r) => r.data),
  deleteUser: (id) => adminClient.delete(`/admin/users/${id}`).then((r) => r.data),
  // venues
  createVenue: (payload) => adminClient.post("/admin/venues", payload).then((r) => r.data),
  updateVenue: (id, payload) => adminClient.put(`/admin/venues/${id}`, payload).then((r) => r.data),
  deleteVenue: (id) => adminClient.delete(`/admin/venues/${id}`).then((r) => r.data),
  // coupons
  createCoupon: (payload) => adminClient.post("/admin/coupons", payload).then((r) => r.data),
  updateCoupon: (id, payload) => adminClient.put(`/admin/coupons/${id}`, payload).then((r) => r.data),
  deleteCoupon: (id) => adminClient.delete(`/admin/coupons/${id}`).then((r) => r.data),
  // reviews
  listReviews: () => adminClient.get("/admin/reviews").then((r) => r.data),
  deleteReview: (id) => adminClient.delete(`/admin/reviews/${id}`).then((r) => r.data),
  // exports
  exportClaimsUrl: () => `${API}/admin/exports/claims.csv`,
  // generic helpers for custom routes
  get: (path) => adminClient.get(path).then((r) => r.data),
  post: (path, body) => adminClient.post(path, body).then((r) => r.data),
  put: (path, body) => adminClient.put(path, body).then((r) => r.data),
  del: (path) => adminClient.delete(path).then((r) => r.data),
};

export function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

const AuthCtx = createContext(null);

export function AdminAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null); // null=loading, false=anon, object=logged in
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = getToken();
    if (!t) {
      setAdmin(false);
      setLoading(false);
      return;
    }
    adminApi
      .me()
      .then((d) => setAdmin(d))
      .catch(() => setAdmin(false))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const res = await adminApi.login(email, password);
    setToken(res.token);
    setAdmin(res.admin);
    return res.admin;
  };

  const logout = () => {
    clearToken();
    setAdmin(false);
    window.location.href = "/admin/login";
  };

  return <AuthCtx.Provider value={{ admin, loading, login, logout, refresh: () => adminApi.me().then(setAdmin) }}>{children}</AuthCtx.Provider>;
}

export function useAdminAuth() {
  return useContext(AuthCtx);
}
